import { useEffect, useState, useContext, useRef } from 'react';
import { useTracksStore } from '../store/tracks.store';
import { usePublicTracksStore } from '../store/publicTracks.store';
import { useOrganizationStore } from '../store/organization.store';
import { FirebaseContext } from '../components/Firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getOrgCollectionPath } from '../constants/paths';

const TracksListener: React.FC = () => {
  const { initTracks, clearTracks } = useTracksStore();
  const firebase = useContext(FirebaseContext);
  const [user, setUser] = useState<any>(null);
  const refreshedForOrg = useRef<string | null>(null);

  // Get current organization from the store
  const currentOrgId = useOrganizationStore(state => state.currentOrgId);
  const isOrgLoading = useOrganizationStore(state => state.isLoading);

  useEffect(() => {
    if (!firebase) return;
    const unsubscribe = firebase.auth.onAuthStateChanged(async (user: any) => {
      if (user) {
        // Force refresh the ID token to ensure Firestore has valid auth
        await user.getIdToken(true);
        setUser(user);
      } else {
        setUser(null);
        refreshedForOrg.current = null;
        // Clear tracks on logout
        clearTracks();
        // Also tear down the global publicCatalogue subscription so a
        // subsequent login re-initialises cleanly for the new user.
        usePublicTracksStore.getState().clearTracks();
      }
    });

    return () => unsubscribe();
  }, [firebase, clearTracks]);

  // Initialize or switch tracks when user is logged in
  // This effect handles both initial load and organization switching
  useEffect(() => {
    if (user && !isOrgLoading && firebase) {
      // Initialize/switch tracks for the current organization
      initTracks(firebase, currentOrgId);
      // Also subscribe globally to /publicCatalogue so the per-track
      // "Make Public" toggle (rendered on the current org's library)
      // can derive its state from a single source of truth. Without
      // this, the toggle would rely on per-org `isPublic` mirroring
      // that can drift when the cross-org cascade misses a copy.
      // `initTracks` on this store is idempotent (isInitialized guard).
      usePublicTracksStore.getState().initTracks(firebase);
    }
  }, [user, firebase, isOrgLoading, currentOrgId, initTracks]);

  // One-time refresh of delivery tracks from source orgs (picks up QC/review changes)
  // Uses a subscription to detect when tracks are loaded, then refreshes once
  useEffect(() => {
    if (!currentOrgId || !firebase) return;

    // Subscribe to tracks store — wait for tracks to load, then refresh delivery tracks once
    const unsubRefresh = useTracksStore.subscribe((state) => {
      const allTracks = state.tracks;
      const count = Object.keys(allTracks).length;
      if (count === 0 || refreshedForOrg.current === currentOrgId) return;

      refreshedForOrg.current = currentOrgId;
      // Unsubscribe immediately — we only need this once
      unsubRefresh();

      // Sync any received copy back from its source org — delivery copies
      // (existing) AND campaign copies (new) need their qc/approval/file
      // state mirrored from the artist's authoritative audiometa doc.
      const receivedCopies = Object.values(allTracks).filter(
        (t: any) => (t.isDeliveryTrack || t.isCampaignCopy) && t.sourceOrgId && t.sourceOrgId !== currentOrgId
      );
      if (receivedCopies.length === 0) return;

      (async () => {
        for (const localTrack of receivedCopies) {
          try {
            const sourceRef = doc(firebase.db, getOrgCollectionPath((localTrack as any).sourceOrgId, 'audiometa'), (localTrack as any).id);
            const sourceSnap = await getDoc(sourceRef);
            if (!sourceSnap.exists()) continue;
            const sourceData = sourceSnap.data();
            const updates: any = {};
            if (sourceData.qc_status && sourceData.qc_status !== (localTrack as any).qc_status) updates.qc_status = sourceData.qc_status;
            if (sourceData.approval_status && sourceData.approval_status !== (localTrack as any).approval_status) updates.approval_status = sourceData.approval_status;
            if (sourceData.is_broadcast_master !== undefined && sourceData.is_broadcast_master !== (localTrack as any).is_broadcast_master) updates.is_broadcast_master = sourceData.is_broadcast_master;
            if (sourceData.fileVersion && sourceData.fileVersion !== (localTrack as any).fileVersion) updates.fileVersion = sourceData.fileVersion;
            if (sourceData.qcVersion && sourceData.qcVersion !== (localTrack as any).qcVersion) updates.qcVersion = sourceData.qcVersion;
            if (sourceData.mp3Files) updates.mp3Files = sourceData.mp3Files;
            if (sourceData.wavFiles) updates.wavFiles = sourceData.wavFiles;
            if (Object.keys(updates).length > 0) {
              const localRef = doc(firebase.db, getOrgCollectionPath(currentOrgId!, 'audiometa'), (localTrack as any).id);
              await updateDoc(localRef, updates);
            }
          } catch { /* best effort */ }
        }
      })();
    });

    return () => unsubRefresh();
  }, [currentOrgId, firebase]);

  return null;
};

export default TracksListener;
